import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {usePostHog} from '@posthog/react';
import {useSettings} from '@common/core/settings/use-settings';
import {
  isReplayRoute,
  resolveReplaySampleRate,
  shouldSampleReplaySession,
} from './posthog-options';

/**
 * Starts PostHog session replay on every route, subject to the configured
 * sample rate. Calls are idempotent across Strict Mode and repeated
 * navigations.
 */
export function PostHogSessionReplayController() {
  const posthog = usePostHog();
  const {pathname} = useLocation();
  const {logging} = useSettings();
  const sampleRate = resolveReplaySampleRate(
    logging.posthog?.session_replay_sample_rate,
  );

  useEffect(() => {
    if (!posthog) {
      return;
    }

    const syncRecording = () => {
      const shouldRecord =
        isReplayRoute(pathname) && shouldSampleReplaySession(sampleRate);
      const isRecording =
        typeof posthog.sessionRecordingStarted === 'function'
          ? posthog.sessionRecordingStarted()
          : false;

      if (shouldRecord && !isRecording) {
        // Override PostHog project sampling; app settings own the rate.
        posthog.startSessionRecording(true);
      } else if (!shouldRecord && isRecording) {
        posthog.stopSessionRecording();
      }
    };

    // PostHogProvider inits in a parent useEffect. Child effects run first, so
    // a synchronous start here races init()'s disable_session_recording: true.
    // Defer until after that init when the client is not loaded yet.
    if (posthog.__loaded) {
      syncRecording();
      return;
    }

    const timeoutId = window.setTimeout(syncRecording, 0);
    return () => window.clearTimeout(timeoutId);
  }, [pathname, posthog, sampleRate]);

  return null;
}
